'use client';

import { useState, useCallback } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import * as styles from './image-crop.css';
import toast from 'react-hot-toast';
import { useLocale, CustomI18n } from '@instamenu/context-i18n';
import { AspectRatioSelect } from './aspect-ratio-select';
import { SparkleButton } from '../sparkle-button/sparkle-button';
import { Box } from '../box/box';
import { Button } from '../button/button';
import { Flex } from '../flex/flex';
import { cropImage } from './crop-image';
import { reframeImage } from './reframe-image';

export type AspectRatioOption = {
  label: string;
  key: string;
};

// Maps aspect ratio strings to their decimal values for the Cropper library.
// '21:9' uses the actual cover crop ratio (5.56) rather than mathematical 21/9,
// since Replicate only supports 21:9 as the closest format.
export const ASPECT_RATIO_DECIMALS: Record<string, number> = {
  '1:1': 1,
  '3:4': 3 / 4,
  '5:4': 5 / 4,
  '9:16': 9 / 16,
  '16:9': 16 / 9,
  '21:9': 5.56,
};

export function aspectRatioToDecimal(ratio: string): number {
  if (ratio in ASPECT_RATIO_DECIMALS) return ASPECT_RATIO_DECIMALS[ratio];
  const [w, h] = ratio.split(':').map(Number);
  return w / h;
}

const getAspectRatioOptions = (i18n: CustomI18n): AspectRatioOption[] => [
  { label: i18n.t('settings.restaurantSettings.aspectRatios.logo'), key: '1:1' },
  { label: i18n.t('settings.restaurantSettings.aspectRatios.portrait'), key: '3:4' },
  { label: i18n.t('settings.restaurantSettings.aspectRatios.kioskBackground'), key: '5:4' },
  { label: i18n.t('settings.restaurantSettings.aspectRatios.story'), key: '9:16' },
  { label: i18n.t('settings.restaurantSettings.aspectRatios.cover'), key: '21:9' },
];

// Cover images need extra vertical padding to ensure more of the image is
// visible when the viewport narrows on mobile devices
const COVER_ASPECT_RATIO = '21:9';
const COVER_VERTICAL_PADDING = 100;

type ImageCropProps = {
  imageSrc: string;
  setImageSrc: (src: string | null) => void;
  aspect?: string;
  showAspectRatioSelector?: boolean;
  onCrop?: (dataUri: string) => void | Promise<void>;
  padding?: number;
};

export const ImageCrop = ({
  imageSrc,
  setImageSrc,
  aspect: initialAspect = '1:1',
  showAspectRatioSelector = false,
  onCrop,
  padding,
}: ImageCropProps) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [selectedAspect, setSelectedAspect] = useState<string>(initialAspect);
  const { i18n } = useLocale();
  const [isUploading, setIsUploading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const aspectRatioOptions = getAspectRatioOptions(i18n);

  const currentAspect = showAspectRatioSelector ? selectedAspect : initialAspect;
  const currentAspectDecimal = aspectRatioToDecimal(currentAspect);

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleApply = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setIsUploading(true);
    try {
      const appliedPadding =
        currentAspect === COVER_ASPECT_RATIO ? COVER_VERTICAL_PADDING : padding;
      const croppedImg = await cropImage(imageSrc, croppedAreaPixels, appliedPadding);

      await onCrop?.(croppedImg);
      setImageSrc(null);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error(i18n.t('settings.restaurantSettings.failedToProcess'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleAIEnhance = async () => {
    if (!imageSrc) return;
    setIsEnhancing(true);
    try {
      const result = await reframeImage({
        imageSrc,
        aspectRatio: currentAspect,
      });
      if (result.success && result.imageUrl) {
        setImageSrc(result.imageUrl);
      } else {
        throw new Error(result.error || 'Failed to generate enhanced image');
      }
    } catch (error) {
      console.error('Error enhancing image:', error);
      toast.error(i18n.t('settings.restaurantSettings.failedToEnhance'));
    } finally {
      setIsEnhancing(false);
    }
  };

  return (
    <Box className={styles.cropperSection}>
      <Box className={styles.cropperContainer}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={currentAspectDecimal}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          style={{
            containerStyle: {
              width: '100%',
              height: '100%',
              backgroundColor: 'transparent',
            },
          }}
        />
        <Box className={styles.controlsOverlay}>
          <label className={styles.controlLabel}>
            {i18n.t('settings.restaurantSettings.zoom')}
          </label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={styles.rangeInput}
          />
        </Box>
        <Flex css={{ position: 'absolute', top: 12, right: 12 }}>
          <Flex css={{ gap: 8, alignItems: 'center' }}>
            {showAspectRatioSelector && (
              <AspectRatioSelect
                value={selectedAspect}
                onChange={setSelectedAspect}
                options={aspectRatioOptions}
              />
            )}
            <SparkleButton loading={isEnhancing} onClick={handleAIEnhance}>
              {i18n.t('settings.restaurantSettings.smartCrop')}
            </SparkleButton>
          </Flex>
        </Flex>
      </Box>
      <Box className={styles.buttonGroup}>
        <Button loading={isEnhancing || isUploading} onClick={handleApply} variant="primary">
          {i18n.t('settings.restaurantSettings.save')}
        </Button>
        <Button onClick={() => setImageSrc(null)} variant="secondary">
          {i18n.t('settings.restaurantSettings.cancel')}
        </Button>
      </Box>
    </Box>
  );
};
